import React, { useState } from 'react';
import { Collapse, Tag, Tooltip } from 'antd';
import { CaretRightOutlined, DatabaseOutlined, RobotOutlined } from '@ant-design/icons';
import Markdown from 'markdown-to-jsx';
import { useMsal } from '@azure/msal-react';
import { IMessage, ISqlQueryAudit } from '../../../services/aiChat/aiChat.service';

const { Panel } = Collapse;

interface MessageBubbleProps {
  message: IMessage;
}

const parseSqlQueries = (raw?: string): ISqlQueryAudit[] => {
  if (!raw) return [];
  try { return JSON.parse(raw); } catch { return []; }
};

const formatDate = (dateStr: string): string => {
  const d = new Date(dateStr);
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatTokens = (n: number): string => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n));

const getInitials = (name: string): string =>
  name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);

const MessageBubble: React.FC<MessageBubbleProps> = ({ message }) => {
  const isUser = message.role === 'user';
  const sqlQueries = parseSqlQueries(message.sql_queries);
  const [sqlExpanded, setSqlExpanded] = useState(false);
  const { accounts } = useMsal();
  // older @azure/msal-react types omit 'name'; cast to access it
  const displayName: string = (accounts[0] as any)?.name ?? accounts[0]?.username ?? 'Me';
  const initials = getInitials(displayName);

  return (
    <div
      style={{
        display: 'flex',
        gap: 14,
        padding: '20px 32px',
        flexDirection: isUser ? 'row-reverse' : 'row',
        background: isUser ? '#ffffff' : '#f9fafb',
        borderTop: isUser ? 'none' : '1px solid #e5e7eb',
        borderBottom: isUser ? 'none' : '1px solid #e5e7eb',
        animation: 'msg-fadein 0.2s ease',
      }}
    >
      {/* Avatar */}
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          marginTop: 2,
          fontSize: isUser ? 13 : 18,
          fontWeight: 700,
          background: isUser ? '#111827' : 'linear-gradient(135deg, #1890ff, #096dd9)',
          color: '#fff',
          letterSpacing: isUser ? '0.5px' : undefined,
          boxShadow: isUser ? 'none' : '0 2px 8px rgba(24, 144, 255, 0.32)',
        }}
      >
        {isUser ? initials : <RobotOutlined />}
      </div>

      {/* Body */}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: isUser ? 'flex-end' : 'flex-start',
        }}
      >
        {isUser ? (
          <div
            style={{
              background: '#1890ff',
              color: '#fff',
              padding: '10px 16px',
              borderRadius: '18px 18px 4px 18px',
              maxWidth: '72%',
              lineHeight: 1.6,
              wordBreak: 'break-word',
              boxShadow: '0 2px 10px rgba(24, 144, 255, 0.22)',
              fontSize: 14,
            }}
          >
            {message.content}
          </div>
        ) : (
          <div className="ai-chat-markdown">
            <Markdown>{message.content}</Markdown>
          </div>
        )}

        {!isUser && sqlQueries.length > 0 && (
          <div className="ai-chat-sql-section">
            <button className="ai-chat-sql-toggle" onClick={() => setSqlExpanded((v) => !v)}>
              <DatabaseOutlined />
              {sqlExpanded ? 'Hide' : 'Show'} SQL ({sqlQueries.length}{' '}
              {sqlQueries.length === 1 ? 'query' : 'queries'})
            </button>
            {sqlExpanded && (
              <Collapse
                ghost
                expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
              >
                {sqlQueries.map((q, i) => (
                  <Panel
                    key={i}
                    header={
                      <span className="ai-chat-sql-header">
                        Query {i + 1}
                        <Tag color={q.capped ? 'orange' : 'green'} style={{ marginLeft: 8 }}>
                          {q.row_count.toLocaleString()} rows{q.capped ? ' · capped at 500' : ''}
                        </Tag>
                      </span>
                    }
                  >
                    <pre className="ai-chat-sql-code">{q.sql}</pre>
                  </Panel>
                ))}
              </Collapse>
            )}
          </div>
        )}

        {/* Meta: token pills + timestamp */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            marginTop: 8,
            flexWrap: 'wrap',
            justifyContent: isUser ? 'flex-end' : 'flex-start',
          }}
        >
          {!isUser && (message.input_tokens_used != null || message.output_tokens_used != null) && (
            <Tooltip title="Input tokens · Output tokens">
              <span className="ai-chat-token-pill">
                {message.input_tokens_used != null && `↑ ${formatTokens(message.input_tokens_used)}`}
                {message.input_tokens_used != null && message.output_tokens_used != null && ' · '}
                {message.output_tokens_used != null && `↓ ${formatTokens(message.output_tokens_used)}`}
              </span>
            </Tooltip>
          )}
          <span className="ai-chat-msg-time">{formatDate(message.created_date)}</span>
        </div>
      </div>
    </div>
  );
};

export default MessageBubble;
